import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Plus, Receipt } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common/PageHeader";
import { EmptyState } from "@/components/common/EmptyState";
import { StatusBadge } from "@/components/common/StatusBadge";
import { SetupBanner, ValidatorChips } from "@/components/finance/SetupBanner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STATUSES = ["draft", "ready", "validation_error", "finalized", "exported", "cancelled", "historical"];

export default function EInvoices() {
  const { t, fmtCurrency, fmtDate } = useI18n();
  const { can } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState("all");

  const { data, isLoading } = useQuery({
    queryKey: ["einvoices", status],
    queryFn: async () => (await api.get("/einvoice/invoices", { params: { status: status === "all" ? "" : status } })).data,
  });

  return (
    <div>
      <PageHeader title={t("einvoice.title")} subtitle={t("einvoice.subtitle")}
        actions={<div className="flex items-center gap-3"><ValidatorChips />
          {can("einvoice:create") && (
            <Button onClick={() => navigate("/finance/einvoices/new")} data-testid="new-einvoice-button">
              <Plus className="h-4 w-4" />{t("einvoice.new")}
            </Button>)}
        </div>} />

      <SetupBanner />

      <div className="mb-4 flex">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-full sm:w-56" data-testid="einvoice-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("common.all")}</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{t(`einvoice.status.${s}`)}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Card>
        {isLoading ? (
          <div className="space-y-2 p-4">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-10" />)}</div>
        ) : data.length === 0 ? (
          <EmptyState icon={Receipt} title={t("einvoice.empty")} testId="einvoices-empty"
            action={can("einvoice:create") && <Button onClick={() => navigate("/finance/einvoices/new")}><Plus className="h-4 w-4" />{t("einvoice.new")}</Button>} />
        ) : (
          <Table data-testid="einvoices-table">
            <TableHeader><TableRow>
              <TableHead>{t("einvoice.number")}</TableHead>
              <TableHead>{t("einvoice.customer")}</TableHead>
              <TableHead className="hidden md:table-cell">{t("einvoice.date")}</TableHead>
              <TableHead className="text-right">{t("einvoice.total")}</TableHead>
              <TableHead>{t("common.status")}</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {data.map((e) => (
                <TableRow key={e.id} className="cursor-pointer" onClick={() => navigate(`/finance/einvoices/${e.id}`)} data-testid={`einvoice-row-${e.id}`}>
                  <TableCell className="font-medium tabular-nums">{e.number || t("einvoice.draft_no")}
                    {e.doc_type === "credit_note" && <span className="ml-1 rounded bg-violet-100 px-1 text-xs text-violet-700 dark:bg-violet-950 dark:text-violet-300">GS</span>}</TableCell>
                  <TableCell>{e.buyer_name || "—"}</TableCell>
                  <TableCell className="hidden text-slate-500 md:table-cell">{e.issue_date ? fmtDate(e.issue_date) : "—"}</TableCell>
                  <TableCell className="text-right tabular-nums font-medium">{fmtCurrency(e.totals?.gross_total)}</TableCell>
                  <TableCell><StatusBadge status={e.status} labelKey={`einvoice.status.${e.status}`} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}
